import { $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
  LexicalEditor,
} from 'lexical';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Close, Edit, Link as LinkIcon } from '@mui/icons-material';
import classes from './FloatingLinkEditorPlugin.module.scss';

function getSelectedNode(selection: any) {
  const { anchor, focus } = selection;
  const anchorNode = anchor.getNode();
  const focusNode = focus.getNode();
  if (anchorNode === focusNode) return anchorNode;
  return selection.isBackward() ? focusNode : anchorNode;
}

/**
 * Normalize a user-entered URL: trim, leave a present scheme alone, turn an
 * email into mailto:, otherwise assume https://. (Prior attempt stored raw,
 * unvalidated input — e.g. "example.com" produced a relative link.)
 */
function normalizeUrl(raw: string): string {
  const url = raw.trim();
  if (url === '') return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url; // has a scheme
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) return `mailto:${url}`;
  return `https://${url}`;
}

function FloatingLinkEditor({
  editor,
  anchorElem,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [lastSelection, setLastSelection] = useState<any>(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);
      if (linkParent) {
        setLinkUrl(linkParent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
    }

    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const { activeElement } = document;

    if (!editorElem) return;

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect = nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      const anchorRect = anchorElem.getBoundingClientRect();
      if (domRect) {
        editorElem.style.top = `${domRect.bottom - anchorRect.top + 8}px`;
        editorElem.style.left = `${domRect.left - anchorRect.left}px`;
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== classes.linkInput) {
      editorElem.style.top = '-1000px';
      editorElem.style.left = '-1000px';
      setLastSelection(null);
      setIsEditMode(false);
      setLinkUrl('');
    }
  }, [anchorElem, editor]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;
    const update = () => {
      try {
        editor.read(() => updateLinkEditor());
      } catch {
        // Editor may not be active (resize/scroll during mode switch)
      }
    };
    window.addEventListener('resize', update);
    if (scrollerElem) scrollerElem.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) scrollerElem.removeEventListener('scroll', update);
    };
  }, [anchorElem.parentElement, editor, updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        try {
          editor.read(() => updateLinkEditor());
        } catch {
          // Editor may not be active
        }
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          try {
            editor.read(() => updateLinkEditor());
          } catch {
            // Editor may not be active
          }
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isEditMode) {
            setIsEditMode(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, updateLinkEditor, isEditMode]);

  useEffect(() => {
    try {
      editor.read(() => updateLinkEditor());
    } catch {
      // Editor may not be ready yet
    }
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  const handleLinkSubmit = () => {
    if (lastSelection !== null) {
      const normalized = normalizeUrl(editedLinkUrl);
      if (normalized !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalized);
      } else {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    }
    setIsEditMode(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLinkSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditMode(false);
    }
  };

  return (
    <div ref={editorRef} className={classes.linkEditor}>
      {isEditMode ? (
        <div className={classes.linkInputWrapper}>
          <LinkIcon fontSize="small" className={classes.linkIcon} />
          <input
            ref={inputRef}
            className={classes.linkInput}
            value={editedLinkUrl}
            onChange={(e) => setEditedLinkUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter URL"
          />
          <button onClick={handleLinkSubmit} className={classes.linkBtn} title="Confirm">
            <Check fontSize="small" />
          </button>
          <button onClick={() => setIsEditMode(false)} className={classes.linkBtn} title="Cancel">
            <Close fontSize="small" />
          </button>
        </div>
      ) : (
        <div className={classes.linkView}>
          <LinkIcon fontSize="small" className={classes.linkIcon} />
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className={classes.linkHref}>
            {linkUrl}
          </a>
          <button
            onClick={() => {
              setEditedLinkUrl(linkUrl);
              setIsEditMode(true);
            }}
            className={classes.linkBtn}
            title="Edit link"
          >
            <Edit fontSize="small" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            }}
            className={classes.linkBtn}
            title="Remove link"
          >
            <Close fontSize="small" />
          </button>
        </div>
      )}
    </div>
  );
}

export function FloatingLinkEditorPlugin({ anchorElem }: { anchorElem: HTMLElement }) {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);
      const autoLinkParent = $findMatchingParent(node, $isAutoLinkNode);
      setIsLink(
        linkParent !== null ||
          autoLinkParent !== null ||
          $isLinkNode(node) ||
          $isAutoLinkNode(node),
      );
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerCommand(
      CLICK_COMMAND,
      (payload) => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = getSelectedNode(selection);
          const linkNode = $findMatchingParent(node, $isLinkNode);
          if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
            window.open(linkNode.getURL(), '_blank');
            return true;
          }
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  if (!isLink) return null;

  return createPortal(
    <FloatingLinkEditor editor={activeEditor} anchorElem={anchorElem} />,
    anchorElem,
  );
}
